const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export function getCountdownParts(targetIso, nowMs = Date.now()) {
  const targetMs = Date.parse(targetIso);
  if (!Number.isFinite(targetMs)) {
    return { valid: false, expired: false, totalMs: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const rawRemainingMs = targetMs - nowMs;
  const totalMs = Math.max(0, rawRemainingMs);

  return {
    valid: true,
    expired: rawRemainingMs <= 0,
    totalMs,
    days: Math.floor(totalMs / DAY_MS),
    hours: Math.floor((totalMs % DAY_MS) / HOUR_MS),
    minutes: Math.floor((totalMs % HOUR_MS) / MINUTE_MS),
    seconds: Math.floor((totalMs % MINUTE_MS) / SECOND_MS),
  };
}

export function countdownUnitLabel(unit, value) {
  return value === 1 ? unit : `${unit}s`;
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class HavocLaunchCountdown extends HTMLElementBase {
  connectedCallback() {
    this.targetIso = this.dataset.target;
    this.timer = this.querySelector('[data-countdown-timer]');
    this.expiredMessage = this.querySelector('[data-countdown-expired]');
    this.invalidMessage = this.querySelector('[data-countdown-invalid]');
    this.valueNodes = new Map(
      [...this.querySelectorAll('[data-countdown-value]')].map((node) => [node.dataset.countdownValue, node]),
    );
    this.labelNodes = new Map(
      [...this.querySelectorAll('[data-countdown-label]')].map((node) => [node.dataset.countdownLabel, node]),
    );

    this.render();
    if (!this.hasAttribute('data-complete')) {
      this.intervalId = window.setInterval(() => this.render(), SECOND_MS);
    }
  }

  disconnectedCallback() {
    if (this.intervalId) window.clearInterval(this.intervalId);
  }

  render(nowMs = Date.now()) {
    const parts = getCountdownParts(this.targetIso, nowMs);

    if (!parts.valid) {
      this.finish('invalid');
      return parts;
    }

    if (parts.expired) {
      this.finish('expired');
      return parts;
    }

    for (const unit of ['days', 'hours', 'minutes', 'seconds']) {
      const value = parts[unit];
      const valueNode = this.valueNodes.get(unit);
      const labelNode = this.labelNodes.get(unit);
      if (valueNode) valueNode.textContent = String(value).padStart(2, '0');
      if (labelNode) labelNode.textContent = countdownUnitLabel(unit.slice(0, -1), value);
    }

    return parts;
  }

  finish(state) {
    this.setAttribute('data-complete', '');
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    if (this.timer) this.timer.hidden = true;
    if (this.expiredMessage) this.expiredMessage.hidden = state !== 'expired';
    if (this.invalidMessage) this.invalidMessage.hidden = state !== 'invalid';
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('havoc-launch-countdown')) {
  customElements.define('havoc-launch-countdown', HavocLaunchCountdown);
}
